import { useMemo } from "react";

import { transform } from "lodash";
import { twMerge } from "tailwind-merge";
import { StringParam, useQueryParam } from "use-query-params";
import { useLocalStorage } from "usehooks-ts";

import { PrimaryButton } from "@/components/shared/buttons";
import appLovinLogo from "@assets/applovin-logo.svg";
import tiktokLogo from "@assets/tiktok-gradient-icon.svg";
import { useUser } from "@components/user-context";
import { usePartnerConsent } from "@features/partner-consent/hooks/use-partner-consent";
import { useFeatureFlags } from "@hooks/http";
import { Partner, PartnerPermissionEnum } from "@north-beam/nb-client-side";
import { FEATURE_FLAG_FOR_PARTNER_CONSENT_MAPPING } from "@north-beam/nb-common/constants";

export const CLICKS_AND_VIEWS_ENHANCED_MODEL = "northbeam_custom__enh";

export const PARTNER_TO_INFO_MAPPING = {
  APPLOVIN: {
    logo: appLovinLogo,
    backgroundClassName: "bg-cosmic-3",
    displayName: "AppLovin",
    featureFlag: FEATURE_FLAG_FOR_PARTNER_CONSENT_MAPPING["APPLOVIN"],
  },
  TIKTOK: {
    logo: tiktokLogo,
    backgroundClassName: "bg-venus-3",
    displayName: "TikTok",
    featureFlag: FEATURE_FLAG_FOR_PARTNER_CONSENT_MAPPING["TIKTOK"],
  },
};

const DEFAULT_INFO_FOR_NO_ENABLED_PARTNERS = {
  backgroundClassName: "bg-venus-3",
};

export const useUpsellEnhancedModel = () => {
  const [, setAttributionModel] = useQueryParam(
    "attributionModel",
    StringParam,
    { updateType: "pushIn" },
  );

  const { hasFeatureFlag } = useFeatureFlags();
  const { user } = useUser();
  const [showUpsellBanner, setShowUpsellBanner] = useLocalStorage(
    `show-enhanced-clicks-views-banner-${user.displayUserId}`,
    true,
  );

  // TODO: Add new endpoint to check consent for all partners supporting Clicks + Deterministic Views Model
  const { hasProvidedConsent: hasProvidedAppLovinConsent } = usePartnerConsent({
    user,
    partner: Partner.APPLOVIN,
    permissions: [PartnerPermissionEnum.data_impression_ingestion],
  });

  const { hasProvidedConsent: hasProvidedTikTokConsent } = usePartnerConsent({
    user,
    partner: Partner.TIKTOK,
    permissions: [PartnerPermissionEnum.data_impression_ingestion],
  });

  const PARTNERS_CONSENT_MAPPING = {
    APPLOVIN: hasProvidedAppLovinConsent,
    TIKTOK: hasProvidedTikTokConsent,
  };

  const enabledPartnersForEnhancedModel = transform<
    typeof PARTNERS_CONSENT_MAPPING,
    (keyof typeof PARTNERS_CONSENT_MAPPING)[]
  >(
    PARTNERS_CONSENT_MAPPING,
    (enabledPartners, hasProvidedConsent, partner) => {
      if (hasProvidedConsent) {
        enabledPartners.push(partner);
      }
    },
    [],
  );

  const shouldDisplayUpsellBanner = useMemo(() => {
    if (!showUpsellBanner) {
      return false;
    }

    const forceDisplayEnhancedModelFF = hasFeatureFlag("enableCVEOverride");
    if (forceDisplayEnhancedModelFF) {
      return true;
    }

    const userHasEnhancedModel = user.attributionModels.find(
      (m) => m.id === CLICKS_AND_VIEWS_ENHANCED_MODEL,
    );

    const hasAnyEnabledPartner = enabledPartnersForEnhancedModel.length > 0;
    if (!hasAnyEnabledPartner) {
      return false;
    }

    if (!userHasEnhancedModel) {
      return false;
    }

    return true;
  }, [
    showUpsellBanner,
    hasFeatureFlag,
    enabledPartnersForEnhancedModel,
    user.attributionModels,
  ]);

  const hasNoEnabledPartner = enabledPartnersForEnhancedModel.length === 0;
  const hasMultipleEnabledPartners = enabledPartnersForEnhancedModel.length > 1;

  const partnersDisplayNames = enabledPartnersForEnhancedModel
    .map((partner) => PARTNER_TO_INFO_MAPPING[partner].displayName)
    .join(" & ");

  const upsellHeaderText = `Clicks + Deterministic Views available${
    !partnersDisplayNames ? "" : ` for ${partnersDisplayNames}`
  }!`;

  const partnersDescriptionForBodyText = ` directly from ${
    hasMultipleEnabledPartners ? "these platforms" : partnersDisplayNames
  }`;
  const upsellBodyText = `See how views lead to purchases with enhanced data${
    hasNoEnabledPartner ? "" : partnersDescriptionForBodyText
  }.`;

  const tryEnhancedModelButtonInfo = {
    text: "TRY THIS MODEL",
    onClick: () => setAttributionModel(CLICKS_AND_VIEWS_ENHANCED_MODEL),
    className: "font-semibold bg-primary hover:bg-cosmic+2",
  };

  const backgroundClassName = hasMultipleEnabledPartners
    ? "bg-gradient-to-r from-venus-2 to-cosmic-3"
    : PARTNER_TO_INFO_MAPPING[enabledPartnersForEnhancedModel[0]]
        ?.backgroundClassName ??
      DEFAULT_INFO_FOR_NO_ENABLED_PARTNERS.backgroundClassName;

  const partnersForEnhancedModelLogosWithOverlay =
    hasNoEnabledPartner ? null : (
      <div className="flex -space-x-1">
        {enabledPartnersForEnhancedModel
          .map((partner, index) => {
            const partnerInfo = PARTNER_TO_INFO_MAPPING[partner];
            return (
              <div
                key={index}
                className={twMerge(
                  `flex items-center justify-center rounded-full p-1
                    h-6 min-h-[1.5rem] min-w-[1.5rem] w-6 border-gray-1.5 border-[0.5px]`,
                  partnerInfo.backgroundClassName,
                )}
              >
                <img
                  className="w-3 h-3"
                  alt={partnerInfo.displayName}
                  src={partnerInfo.logo}
                />
              </div>
            );
          })
          .reverse()}
      </div>
    );

  const tryEnhancedModelCard = (
    <div className="w-[21rem] rounded-full">
      <div className={twMerge("h-2 rounded-t-md", backgroundClassName)}></div>
      <div className="mx-4 my-4">
        <div className="items-center text-lg font-semibold flex gap-2">
          {partnersForEnhancedModelLogosWithOverlay}
          <span>Clicks + Deterministic Views</span>
        </div>
        <div className="mt-4 text-sm">
          <span className="font-semibold">{upsellHeaderText}</span>{" "}
          {upsellBodyText}
        </div>
        <div className="mt-3 flex items-center gap-5">
          <PrimaryButton
            className={twMerge(
              "font-semibold ml-auto py-2 rounded-full font-sans",
              tryEnhancedModelButtonInfo.className,
            )}
          >
            {tryEnhancedModelButtonInfo.text}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
  return {
    shouldDisplayUpsellBanner,
    upsellHeaderText,
    upsellBodyText,
    enabledPartnersForEnhancedModel,
    onDismissUpsellBanner: () => setShowUpsellBanner(false),
    tryEnhancedModelButtonInfo,
    backgroundClassName,
    partnersForEnhancedModelLogosWithOverlay,
    tryEnhancedModelCard,
  };
};
